import { useCallback, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

// Types for keyboard shortcut registration
export type KeyCombo = string; // e.g. 'ctrl+s', 'shift+?', 'meta+k'
export type ShortcutHandler = (event: KeyboardEvent) => void;
export type ShortcutScope = string;

export interface ShortcutRegistration {
  id: string;
  combo: KeyCombo;
  handler: ShortcutHandler;
  description: string;
  scope: ShortcutScope;
  disabled?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  allowInInputs?: boolean;
  priority?: number; // Higher numbers have higher priority
}

// Normalize key combo strings
const normalizeKeyCombo = (combo: KeyCombo): KeyCombo => {
  // Split the combo by '+', sort modifiers alphabetically, and rejoin
  const parts = combo.toLowerCase().split('+');
  const key = parts.pop() || '';
  const modifiers = parts.sort();

  return [...modifiers, key].join('+');
};

// Parse a keyboard event to a normalized key combo
const eventToKeyCombo = (event: KeyboardEvent): KeyCombo => {
  const modifiers: string[] = [];
  if (event.ctrlKey) modifiers.push('ctrl');
  if (event.shiftKey) modifiers.push('shift');
  if (event.altKey) modifiers.push('alt');
  if (event.metaKey) modifiers.push('meta');

  const key = event.key ? event.key.toLowerCase() : 'unknown';
  modifiers.sort();

  return [...modifiers, key].join('+');
};

// Check if an element is an input element where shortcuts should be disabled
const isInputElement = (element: HTMLElement | null): boolean => {
  if (!element) return false;

  const tagName = element.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  const isEditable = element.isContentEditable;

  return isInput || isEditable;
};

/**
 * Hook to register keyboard shortcuts
 *
 * @param shortcuts Array of shortcut registrations
 * @param activeScope Current active scope
 * @returns Object with methods to enable/disable shortcuts
 */
export const useKeyboardShortcuts = (
  shortcuts: ShortcutRegistration[],
  activeScope: ShortcutScope = 'global'
) => {
  // Refs to prevent unnecessary re-renders and maintain state
  const registeredShortcuts = useRef<Map<string, ShortcutRegistration>>(new Map());
  const activeShortcutIds = useRef<Set<string>>(new Set());

  // Disable a specific shortcut by ID
  const disableShortcut = useCallback((id: string) => {
    const shortcut = registeredShortcuts.current.get(id);
    if (shortcut) {
      shortcut.disabled = true;
      registeredShortcuts.current.set(id, shortcut);
    }
  }, []);

  // Enable a specific shortcut by ID
  const enableShortcut = useCallback((id: string) => {
    const shortcut = registeredShortcuts.current.get(id);
    if (shortcut) {
      shortcut.disabled = false;
      registeredShortcuts.current.set(id, shortcut);
    }
  }, []);

  // Add a new shortcut dynamically
  const addShortcut = useCallback(
    (shortcut: ShortcutRegistration) => {
      const normalizedCombo = normalizeKeyCombo(shortcut.combo);
      const normalizedShortcut = { ...shortcut, combo: normalizedCombo };

      registeredShortcuts.current.set(shortcut.id, normalizedShortcut);
      if (shortcut.scope === activeScope || shortcut.scope === 'global') {
        activeShortcutIds.current.add(shortcut.id);
      }

      return () => {
        registeredShortcuts.current.delete(shortcut.id);
        activeShortcutIds.current.delete(shortcut.id);
      };
    },
    [activeScope]
  );

  // Remove a shortcut by ID
  const removeShortcut = useCallback((id: string) => {
    registeredShortcuts.current.delete(id);
    activeShortcutIds.current.delete(id);
  }, []);

  // Get all registered shortcuts
  const getShortcuts = useCallback(() => {
    return Array.from(registeredShortcuts.current.values());
  }, []);

  // Get shortcuts for a specific scope
  const getShortcutsForScope = useCallback((scope: ShortcutScope) => {
    return Array.from(registeredShortcuts.current.values()).filter(
      shortcut => shortcut.scope === scope
    );
  }, []);

  // Handle keyboard events
  useEffect(() => {
    // Register all shortcuts from the prop
    shortcuts.forEach(shortcut => {
      const normalizedCombo = normalizeKeyCombo(shortcut.combo);
      const normalizedShortcut = { ...shortcut, combo: normalizedCombo };
      registeredShortcuts.current.set(shortcut.id, normalizedShortcut);

      if (shortcut.scope === activeScope) {
        activeShortcutIds.current.add(shortcut.id);
      }
    });

    // Clean up when the component unmounts or shortcuts change
    return () => {
      shortcuts.forEach(shortcut => {
        registeredShortcuts.current.delete(shortcut.id);
        activeShortcutIds.current.delete(shortcut.id);
      });
    };
  }, [shortcuts, activeScope]);

  // Update active shortcuts when scope changes
  useEffect(() => {
    // Clear current active shortcuts
    activeShortcutIds.current.clear();

    // Add global shortcuts and shortcuts for the active scope
    Array.from(registeredShortcuts.current.values()).forEach(shortcut => {
      if (shortcut.scope === 'global' || shortcut.scope === activeScope) {
        activeShortcutIds.current.add(shortcut.id);
      }
    });
  }, [activeScope]);

  // Main keyboard event handler
  useEffect(() => {
    // Check if we're in a Capacitor environment
    const isCapacitorApp =
      typeof window !== 'undefined' &&
      window.Capacitor &&
      window.Capacitor.isNativePlatform &&
      window.Capacitor.isNativePlatform();

    // If we're in a Capacitor app, don't register keyboard handlers at all
    if (isCapacitorApp) {
      logger.component('Keyboard shortcuts disabled in Capacitor app');
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if modifier keys are pressed alone (e.g. just Shift with no other key)
      if (event.key && ['Control', 'Shift', 'Alt', 'Meta'].includes(event.key)) {
        return;
      }

      const combo = eventToKeyCombo(event);
      const target = event.target as HTMLElement;
      const isInput = isInputElement(target);

      // Find matching shortcuts, prioritizing higher priority shortcuts
      const matchingShortcuts = Array.from(registeredShortcuts.current.values())
        .filter(
          shortcut =>
            activeShortcutIds.current.has(shortcut.id) &&
            shortcut.combo === combo &&
            !shortcut.disabled &&
            (shortcut.allowInInputs || !isInput)
        )
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));

      if (matchingShortcuts.length > 0) {
        const shortcut = matchingShortcuts[0];

        logger.component('Keyboard shortcut triggered', {
          id: shortcut.id,
          combo: shortcut.combo,
          description: shortcut.description,
        });

        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }

        if (shortcut.stopPropagation !== false) {
          event.stopPropagation();
        }

        shortcut.handler(event);
        return;
      }
    };

    // Only add event listener in web environments, not in Capacitor
    document.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    disableShortcut,
    enableShortcut,
    addShortcut,
    removeShortcut,
    getShortcuts,
    getShortcutsForScope,
  };
};

export default useKeyboardShortcuts;
